/**
 * KimlikDogrulamaModal.jsx — bir kişinin kimlik doğrulama kararı (Onayla / Reddet / Sıfırla)
 *
 * Kullanım: Kişi Listesi'nde kartın "Kimlik" düğmesi ya da vesikalık kutusu (PersonelKart), Kart Yazdır / Personel Ekle
 * künye şeridi ve (#471) Kişi Detay › Kontroller.
 * Servis: kisiAdminService.getKimlikDogrulama / setKimlikDogrulama
 *   · Karar kişi seviyesinde ve kalıcı (fuardan fuara değişmez).
 *   · Red için açıklama zorunlu (maks. 500). Açıklama kutusu kapalı gelir ("Açıklama gir (opsiyonel)"); kayıtlı bir
 *     açıklama varsa, Reddet açıklamasız basılırsa ve Özel durum seçilirse kendiliğinden açılır.
 *   · #470 / backend #469: tek ortak "Kimlik belgesi" alanı (file_type 90) + isteğe bağlı "doğrulama tipi" (Elden görüldü |
 *     Kimlik | Ehliyet | Pasaport | Diğer | Özel durum). Tip karara bağlıdır ve Onayla / Reddet ile `belgeTuru` olarak
 *     gider; yüklenen dosyaya bağlı değildir. Belge varsa kilitli bir kutu (yüklenme tarihi, durum, geçerlilik, "Aç"),
 *     yoksa geçerlilik tarihiyle birlikte yükleme gösterilir. Kimlik / Ehliyet / Pasaport / Diğer, kişide belge yokken
 *     seçilemez (backend 400 verirdi); Elden görüldü ve Özel durum her zaman seçilebilir. Sıfırla tip göndermez
 *     (backend kayıtlı tipi siler).
 *   · WAS (until #470): "Dayanak dosyası" seçicisi kişinin başka belgelerinden birine `fileId` ile işaret ederdi; backend
 *     o işaretçiyi artık yok sayıyor, seçici, `dayanakSecenekleriFromDocuments` ve örnek dosyalar kalktı.
 *   · #664 (backend #663): Pasaport iki adımlıdır. "Kişiyi yüz yüze gördüm" işaretsizse (masa) Onayla/Reddet
 *     PATCH .../kimlik-dogrulama/pasaport-belgesi (setPasaportBelgesi) çağırır: dosyayı uç onaylar/reddeder, kimlik durumu 3
 *     ("Belge onaylı, alanda doğrulanacak") olur, doğrulanmış SAYILMAZ. İşaretliyse (kapı) eski akış + `yuzYuze: true`.
 *     Durum 3 yüklenirse Pasaport seçili gelir ve bir ipucu satırı çıkar. Diğer türlerde kutu yoktur.
 *   · #719: belgesi olan bir pasaport kişisinde (uyrukId 2, kayıtlı pasaportNo ya da durum 3) "Pasaport" bölümü: numara, veren ülke, dosya
 *     kutusu ve "Belgeyi onayla" / "Belgeyi reddet" (= setPasaportBelgesi; masa kontrolü yalnızca burada). Alt çubuk yalnızca KAPININ
 *     kararıdır: Pasaport seçiliyken "Kimliği doğrula" ve Reddet, "Kişiyi yüz yüze gördüm" işaretlenene dek kapalıdır. WAS (#664): işaretsiz
 *     Onayla / Reddet masa çağrısıydı (aynı düğme iki anlam taşırdı). Kimlik / Ehliyet / Diğer değişmedi (backend sorusu Q2 bekliyor).
 *   · ⚠️ Backend `documents{}` içine tip 90'ı yalnızca bir meslek kuralı istiyorsa koyar; bu yüzden belge GET'in
 *     `kimlikBelgesi` alanından okunur ve yüklemeden sonra GET yeniden çağrılır.
 *     Yükleme alanı `person_logo`ya DOĞRUDAN bağlı değildir — dosya tip 90 olarak yüklenir.
 *
 * #352 — "Kimlik Doğrula / Foto Ekle": üstte Kart Yazdır ile ortak künye şeridi (KisiKunyeSeridi: vesikalık, ad, T.C.,
 *   telefon, kimlik durumu, firma, fuar logosu). Vesikalığın köşesindeki kamera düğmesi kişinin VESİKALIĞINI yükler
 *   (POST /personnel/{id}/fotograf, JPG/PNG/WEBP); aşağıdaki "Kimlik belgesi yükle" ise file_type 90 kimlik belgesi —
 *   iki yükleme ayrıdır. Kart Yazdır'ın ÜSTÜNE açılabilir (yığılmış modal) ve listeden de tek başına açılır.
 *
 * Props:
 *   person          Kişi Listesi satırı (en azından personId; name/surname/tcNo/phone/fotografVar/firmalar/kimlikDogrulama
 *                   varsa şeritte görünür; PersonelEkleModal yalnızca { personId, name, surname } verir)
 *   fuarAdi, fuarLogoUrl  şeridin sağındaki fuar (üst çubuktaki fuar); yoksa sağ taraf boş
 *   firmaFiltreId   şeritteki firma satırı: `onFirmaSec(person, firmaFiltreId)` (süzgeçteki/seçili firma önde) + "+N" diğerleri
 *   onSaved         (personId, res) — karar yazıldı
 *   onFotoDegisti   (personId) — vesikalık yüklendi (çağıran satırı/kararı tazeler)
 */
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Input, Label, Row, Col,
} from "reactstrap";
import { ShieldCheck, ShieldX, RotateCcw, Upload, CheckCircle2, XCircle } from "lucide-react";
import MainContext from "../Contexts/MainContext";
import TarihGirdisi from "../Common/TarihGirdisi";
import KisiDosyaLink from "./KisiDosyaLink";
import KisiKunyeSeridi, { KIMLIK_HEX } from "./KisiKunyeSeridi";
import { STATUS_HEX } from "../Projects/StandPills";
import { firmaAdi, onFirmaSec } from "./PersonelFirmaBelgeParcalari";
import {
  getKimlikDogrulama, setKimlikDogrulama, setPasaportBelgesi, uploadKimlikBelgesi, isStaffRefusal, KIMLIK_DURUM,
  KIMLIK_BELGE_TURLERI, kimlikBelgeTuruNormalize, KIMLIK_BELGESI_UZANTILAR, KIMLIK_BELGESI_MAX_MB,
} from "../../services/kisiAdminService";
import { uploadKisiFotograf, getUlkeListesi } from "../../services/kisiKunyeService";
import { reviewPersonelDocument } from "../../services/personnelAdminService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

// Vesikalık türü: KunyeBasligi ile aynı (JPG / PNG / WEBP; sunucu ayrıca içeriği denetler).
const FOTO_TURLERI = /^image\/(jpeg|png|webp)$/i;
const FOTO_UZANTILARI = /\.(jpe?g|png|webp)$/i;

/** Yüklemeden önce istemci tarafı kontrol; hata metni ya da null. */
function kimlikDosyasiHatasi(file) {
  const uzanti = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
  if (!KIMLIK_BELGESI_UZANTILAR.includes(uzanti)) {
    return i18n.t("components:kimlikDogrulama.gecersizDosyaTipiIzinVerilenler", { v: KIMLIK_BELGESI_UZANTILAR.join(", ") });
  }
  if (file.size > KIMLIK_BELGESI_MAX_MB * 1024 * 1024) return i18n.t("components:kimlikDogrulama.dosyaCokBuyukEnFazla", { KIMLIK_BELGESI_MAX_MB });
  if (file.size === 0) return i18n.t("components:kimlikDogrulama.dosyaBos");
  return null;
}

/** "2026-10-19" (ya da "2026-10-19 14:30:00") → yerel kısa tarih; çözülemezse olduğu gibi. */
function tarihYaz(deger) {
  if (!deger) return null;
  const s = String(deger);
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(currentLocaleTag());
}

/** Kimlik belgesi dosyasının kendi durumu (0 Beklemede / 1 Onaylı / 2 Reddedildi; onaylı ama süresi dolmuş "Süresi doldu"). */
function belgeRozeti(belge, t) {
  if (belge.expired) return { label: t("kimlikDogrulama.belgeSuresiDoldu"), hex: STATUS_HEX.danger };
  if (belge.status === 1) return { label: t("kimlikDogrulama.belgeOnayli"), hex: STATUS_HEX.success };
  if (belge.status === 2) return { label: t("kimlikDogrulama.belgeReddedildi"), hex: STATUS_HEX.danger };
  return { label: t("kimlikDogrulama.belgeBeklemede"), hex: STATUS_HEX.warning, ink: "#212529" };
}

// Bu temada `success` gri, `secondary` yeşil — rozet renkleri StandPills'in sabit hex paletinden (KIMLIK_HEX, künye şeridiyle ortak).
export function KimlikBadge({ durum }) {
  const d = KIMLIK_DURUM[durum] ? durum : 0;
  return (
    <span className="badge fw-normal" style={{ backgroundColor: KIMLIK_HEX[d] }}>
      {KIMLIK_DURUM[d].label}
    </span>
  );
}

/** Kayıtlı tip, kişide belge yokken seçili kalamaz (dört belge türü belge ister). */
function gecerliTip(tip, belgeVar) {
  const tur = KIMLIK_BELGE_TURLERI.find((x) => x.value === tip);
  return tur && (belgeVar || !tur.dosyaGerekir) ? tur.value : "";
}

/** #719: pasaport kişisi — Yabancı (uyrukId 2), kayıtlı pasaport numarası ya da masada onaylı belge (durum 3); T.C. kimlikli kişi değil. */
function pasaportKisisiMi(d) {
  return Number(d?.uyrukId) === 2 || Boolean(d?.pasaportNo) || Number(d?.durum) === 3;
}

export default function KimlikDogrulamaModal({
  person, onClose, onSaved, onFotoDegisti, fuarAdi = null, fuarLogoUrl = null, firmaFiltreId = null,
}) {
  const { t } = useTranslation("components");
  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aciklama, setAciklama] = useState("");
  // Açıklama kutusu kapalı gelir; kayıtlı açıklama, Özel durum ve açıklamasız Reddet onu kendiliğinden açar.
  const [aciklamaAcik, setAciklamaAcik] = useState(false);
  const [aciklamaOdak, setAciklamaOdak] = useState(false);
  // Kimliğin neye bakılarak doğrulandığı (KIMLIK_BELGE_TURLERI.value); "" = belirtilmedi.
  const [belgeTuru, setBelgeTuru] = useState("");
  // #394: kimlik / pasaport belgesinin GERÇEK geçerlilik tarihi (yyyy-MM-dd); yüklemeyle birlikte gider.
  const [gecerlilik, setGecerlilik] = useState("");
  // #664 (backend #663): yalnızca Pasaport için. İşaretsiz = belge masada kontrol edilir (durum 3), işaretli = kişi görüldü (durum 1).
  const [yuzYuze, setYuzYuze] = useState(false);
  const [uploading, setUploading] = useState(false);
  // #719: Pasaport bölümünün veren ülke adı (ülke kimliği → ad); liste yalnızca ülke kimliği varken çekilir.
  const [ulkeAdlari, setUlkeAdlari] = useState({});
  // Vesikalık (kamera düğmesi): sayaç şeridin fotoğrafı yeniden okumasını, `yeniYukleme` yükleyen satırını tazeler.
  const [fotoYukleniyor, setFotoYukleniyor] = useState(false);
  const [fotoSurum, setFotoSurum] = useState(0);
  const [yeniYukleme, setYeniYukleme] = useState(null);
  const fileInputRef = useRef(null);
  const acikRef = useRef(true);
  // Yalnızca `file.fuar_id` etiketi için; backend bunu oturumdan okuyor.
  const selectedFuarId = useContext(MainContext)?.selectedFuarId;

  useEffect(() => {
    acikRef.current = true;
    return () => { acikRef.current = false; };
  }, []);

  useEffect(() => {
    let ignore = false;
    getKimlikDogrulama(person.personId)
      .then((d) => {
        if (ignore) return;
        setDetay(d);
        setAciklama(d?.aciklama || "");
        setAciklamaAcik(Boolean(d?.aciklama));
        // #664: durum 3 (belge masada onaylı) Pasaport'u kendiliğinden seçer; zaten doğrulanmış kişi görülmüştür, kutu işaretli gelir.
        // #719: tip kayıtlı değilse pasaport kişisinde de Pasaport seçili gelir (kayıtlı bir tip, örn. Elden görüldü, kalır).
        const alanda = Number(d?.durum) === 3;
        const kayitliTip = kimlikBelgeTuruNormalize(d?.belgeTuru);
        const baslangicTipi = alanda || (!kayitliTip && pasaportKisisiMi(d)) ? "PASAPORT" : kayitliTip;
        setBelgeTuru(gecerliTip(baslangicTipi, Boolean(d?.kimlikBelgesi?.fileId)));
        setYuzYuze(Number(d?.durum) === 1);
      })
      .catch((e) => { if (!ignore) setError(e.message); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [person.personId]);

  const ulkeId = detay?.pasaportUlkeId ?? null;
  useEffect(() => {
    if (ulkeId == null) return undefined;
    let iptal = false;
    Promise.resolve(getUlkeListesi())
      .then((liste) => {
        if (!iptal) setUlkeAdlari(Object.fromEntries((liste || []).map((u) => [String(u.countryId), u.countryName])));
      })
      .catch(() => { /* ad okunamazsa ülke kimliği görünür */ });
    return () => { iptal = true; };
  }, [ulkeId]);

  const belge = detay?.kimlikBelgesi?.fileId ? detay.kimlikBelgesi : null;

  /** Yükleme sonrası: seçimlere dokunmadan yalnızca belgeyi (ve kararı) yeniden okur. */
  async function belgeyiYenidenOku() {
    const d = await getKimlikDogrulama(person.personId);
    if (acikRef.current) setDetay(d);
  }

  async function onDosyaSecildi(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const hata = kimlikDosyasiHatasi(file);
    if (hata) { setError(hata); return; }
    setUploading(true);
    setError(null);
    try {
      // WAS (until #394): const yeniId = await uploadKimlikBelgesi(person.personId, file, { fuarId: selectedFuarId });
      await uploadKimlikBelgesi(person.personId, file, {
        fuarId: selectedFuarId, expiryDate: gecerlilik || null,
      });
      await belgeyiYenidenOku();
    } catch (err) {
      setError(err.message);
    } finally {
      if (acikRef.current) setUploading(false);
    }
  }

  function tipSec(tur) {
    setBelgeTuru((onceki) => (onceki === tur.value ? "" : tur.value));
    if (tur.aciklamaGerekir) setAciklamaAcik(true);
  }

  async function submit(durum) {
    const aciklamaYok = !aciklama.trim();
    if (durum === 2 && aciklamaYok) {
      setAciklamaAcik(true);
      setAciklamaOdak(true);
      setError(t("kimlikDogrulama.reddetmekIcinAciklamaYazin"));
      return;
    }
    // Sıfırla tip göndermez: backend kayıtlı tipi siler (karar geri alınıyor).
    const tur = durum === 0 ? null : KIMLIK_BELGE_TURLERI.find((x) => x.value === belgeTuru) ?? null;
    if (tur?.aciklamaGerekir && aciklamaYok) {
      setAciklamaAcik(true);
      setAciklamaOdak(true);
      setError(t("kimlikDogrulama.ozelDurumIcinAciklamaYazin"));
      return;
    }
    // #514: kimlik BELGESİNE bakılarak doğrulanıyorsa (Kimlik / Ehliyet / Pasaport / Diğer) ve dosya hâlâ Beklemede ise dosya da
    // onaylanır: kart engeli "Kimlik Fotoğrafı" yalnızca onaylı (status 1) dosyayı sayar, doğrulama ise yalnızca kişi sütunlarını yazar.
    // Tarih backend'de zorunlu (bugün ya da sonrası): belgede kayıtlıysa o, yoksa onaylayan görevlinin girdiği.
    const dosyaOnayi = durum === 1 && Boolean(tur?.dosyaGerekir) && belge?.status === 0 && !belge.expired;
    const onayTarihi = dosyaOnayi ? String(belge.expiryDate || gecerlilik || "").slice(0, 10) : "";
    if (dosyaOnayi && (!onayTarihi || onayTarihi < new Date().toLocaleDateString("sv-SE"))) {
      setError(t("kimlikDogrulama.belgeOnayTarihiGerekli"));
      return;
    }
    // #719: alt çubuk yalnızca KAPININ kararıdır — Pasaport'ta kişi görülmeden karar verilmez (düğmeler de kapalı). Masadaki belge
    // kontrolü (setPasaportBelgesi) yalnızca Pasaport bölümünün düğmelerindedir. WAS (#664): işaretsiz Onayla / Reddet masa çağrısıydı.
    const pasaport = tur?.value === "PASAPORT";
    if (pasaport && durum !== 0 && !yuzYuze) return;
    setSaving(true);
    setError(null);
    try {
      // Önce dosya: sonradan doğrulama düşerse yeniden denenebilir, tersi "doğrulandı ama kart engelli" bırakırdı.
      if (dosyaOnayi) await reviewPersonelDocument(belge.fileId, { status: 1, gecerlilikTarihi: onayTarihi });
      // Kapıdaki onay (kişi görüldü): backend Pasaport ONAYINDA yuzYuze ister; red ve diğer türler değişmez.
      const res = await setKimlikDogrulama(person.personId, {
        durum, aciklama: aciklama.trim(), belgeTuru: tur?.value ?? null,
        ...(pasaport && yuzYuze && durum === 1 ? { yuzYuze: true } : {}),
      });
      onSaved?.(person.personId, res);
    } catch (e) {
      setError(isStaffRefusal(e) ? t("kimlikDogrulama.buIslemIcinSakaPersoneli") : e.message);
    } finally {
      setSaving(false);
    }
  }

  /**
   * #719: Pasaport bölümünün iki düğmesi — yüklenmiş pasaport belgesinin MASADA kontrolü (backend #663). Onay dosyayı onaylar ve kimlik
   * durumunu 3 yapar (doğrulanmış SAYILMAZ); red açıklama ister ve dosyayı reddeder. Tarih yalnızca dosya hâlâ Beklemede ise istenir.
   */
  async function belgeKarari(durum) {
    if (durum === 2 && !aciklama.trim()) {
      setAciklamaAcik(true);
      setAciklamaOdak(true);
      setError(t("kimlikDogrulama.reddetmekIcinAciklamaYazin"));
      return;
    }
    const tarih = String(belge?.expiryDate || gecerlilik || "").slice(0, 10);
    if (durum === 1 && belge?.status === 0 && !belge.expired && (!tarih || tarih < new Date().toLocaleDateString("sv-SE"))) {
      setError(t("kimlikDogrulama.belgeOnayTarihiGerekli"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await setPasaportBelgesi(person.personId, durum === 1
        ? { durum, gecerlilikTarihi: tarih || null, aciklama: aciklama.trim() }
        : { durum, aciklama: aciklama.trim() });
      onSaved?.(person.personId, res);
    } catch (e) {
      setError(isStaffRefusal(e) ? t("kimlikDogrulama.buIslemIcinSakaPersoneli") : e.message);
    } finally {
      setSaving(false);
    }
  }

  /** #352: vesikalığı yükler (kimlik BELGESİ değil, bkz. üst not); başarıda şerit fotoğrafı yeniden okur. */
  async function fotoYukle(dosya) {
    if (!FOTO_TURLERI.test(dosya.type) && !FOTO_UZANTILARI.test(dosya.name)) {
      setError(t("kimlikDogrulama.fotoGecersizTip"));
      return;
    }
    setFotoYukleniyor(true);
    setError(null);
    try {
      const res = await uploadKisiFotograf(person.personId, dosya);
      setYeniYukleme(res?.fotografYukleme ?? null);
      setFotoSurum((n) => n + 1);
      onFotoDegisti?.(person.personId);
    } catch (err) {
      setError(err.status == null ? t("kimlikDogrulama.sunucuYanitVermedi") : err.message);
    } finally {
      setFotoYukleniyor(false);
    }
  }

  const busy = loading || saving || uploading || fotoYukleniyor;

  // Şeritteki firma: süzgeçteki/seçili firma önde, kalanı "+N" (satırdaki `firmalar[]`; yoksa satır çıkmaz).
  const firmalar = person.firmalar || [];
  const onFirma = onFirmaSec(person, firmaFiltreId);
  // Karar okunana dek satırdaki değer; o da yoksa durum gizli (PersonelEkleModal satırsız açar, yanlış "doğrulanmadı" yazılmasın).
  const kimlikDurum = detay ? Number(detay.durum) : (person.kimlikDogrulama?.durum != null ? Number(person.kimlikDogrulama.durum) : null);
  const rozet = belge ? belgeRozeti(belge, t) : null;
  // #719: Pasaport bölümü (belgesi olan pasaport kişisi) ve alt çubuğun kapı kuralı (Pasaport seçiliyken kişi görülmeden karar yok).
  const pasaportBolumu = Boolean(belge) && pasaportKisisiMi(detay);
  const pasaportSecili = belgeTuru === "PASAPORT";
  const kapiKilitli = pasaportSecili && !yuzYuze;
  const durumNo = Number(detay?.durum);
  const tarihYazisi = detay?.tarih ? new Date(detay.tarih).toLocaleString(currentLocaleTag()) : "—";

  // Belge kutusu: kilitli kutu + (Beklemede, tarihsiz dosya için) geçerlilik tarihi; belge yoksa yükleme. Pasaport bölümünde de aynı kutu.
  const belgeAlani = belge ? (
    <>
      <div className="d-flex align-items-center flex-wrap gap-2 border rounded px-2 py-2">
        <span className="badge fw-normal" style={{ backgroundColor: rozet.hex, color: rozet.ink }}>{rozet.label}</span>
        {belge.insertDate && <span className="small text-muted">{t("kimlikDogrulama.belgeYuklendi", { tarih: tarihYaz(belge.insertDate) })}</span>}
        {belge.expiryDate && <span className="small text-muted">{t("kimlikDogrulama.belgeGecerlilik", { tarih: tarihYaz(belge.expiryDate) })}</span>}
        <KisiDosyaLink key={belge.fileId} personId={person.personId} fileId={Number(belge.fileId)} label={t("kimlikDogrulama.ac")} className="ms-auto" />
      </div>
      {/* #514: tarihsiz yüklenmiş, Beklemede dosya — onaylayan görevli tarihi girer (#295, #394); belge türüyle Onayla dosyayı da onaylar. */}
      {belge.status === 0 && !belge.expired && !belge.expiryDate && (
        <>
          <Label className="small mb-1 mt-2 d-block" htmlFor="kimlikBelgesiOnayTarihi">{t("kimlikDogrulama.belgeGecerlilikTarihi")}</Label>
          <TarihGirdisi
            id="kimlikBelgesiOnayTarihi" style={{ maxWidth: 220 }}
            value={gecerlilik} min={new Date().toLocaleDateString("sv-SE")}
            onChange={setGecerlilik}
          />
          {!pasaportBolumu && <div className="form-text">{t("kimlikDogrulama.belgeOnayIpucu")}</div>}
        </>
      )}
    </>
  ) : (
    <>
      {/* #394: belgenin gerçek geçerlilik tarihi; yüklemeden ÖNCE girilir, boşsa belge tarih girilene dek Beklemede kalır. */}
      <Label className="small mb-1 d-block" htmlFor="kimlikBelgesiGecerlilik">{t("kimlikDogrulama.belgeGecerlilikTarihi")}</Label>
      <TarihGirdisi
        id="kimlikBelgesiGecerlilik" style={{ maxWidth: 220 }}
        value={gecerlilik} min={new Date().toLocaleDateString("sv-SE")}
        onChange={setGecerlilik}
      />
      <div className="form-text">{t("kimlikDogrulama.belgeGecerlilikIpucu")}</div>
      <div className="d-flex align-items-center gap-2 mt-2">
        <input
          ref={fileInputRef} type="file" className="d-none"
          accept={KIMLIK_BELGESI_UZANTILAR.map((u) => `.${u}`).join(",")}
          onChange={onDosyaSecildi}
        />
        <Button
          color="primary" outline size="sm" disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? <Spinner size="sm" /> : <><Upload size={14} className="me-1" />{t("kimlikDogrulama.kimlikBelgesiYukle")}</>}
        </Button>
      </div>
    </>
  );

  return (
    <Modal isOpen toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{t("kimlikDogrulama.baslik")}</ModalHeader>
      <ModalBody>
        <KisiKunyeSeridi
          person={person}
          kimlikDurum={kimlikDurum}
          fotografVar={Boolean(person.fotografVar) || fotoSurum > 0}
          fotoSurum={fotoSurum}
          fotografYukleme={yeniYukleme ?? person.fotografYukleme ?? null}
          firmaAdi={onFirma ? firmaAdi(onFirma) : null}
          firmaEki={onFirma ? Math.max(firmalar.length - 1, 0) : 0}
          fuarAdi={fuarAdi}
          fuarLogoUrl={fuarLogoUrl}
          fotoYukle={fotoYukle}
          fotoYukleniyor={fotoYukleniyor}
        />

        {loading && <div className="text-center py-3"><Spinner color="primary" /></div>}
        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}

        {detay && (
          <>
            {detay.durum !== 0 && durumNo !== 3 && (detay.dogrulayanAdi || detay.tarih) && (
              <div className="small text-muted mb-3">
                {detay.dogrulayanAdi ?? "—"}
                {detay.tarih ? ` · ${tarihYazisi}` : ""}
              </div>
            )}

            {pasaportBolumu ? (
              /* #719: belgesi olan pasaport kişisi — masadaki belge kontrolü (numara + ülke + dosya + iki düğme) kendi bölümünde; alt çubuk yalnızca kapının kararı. */
              <div className="border rounded p-3" id="kimlikPasaportBolumu">
                <Label className="small mb-2 d-block fw-semibold">{t("kimlikDogrulama.pasaport")}</Label>
                <Row className="g-2 mb-2">
                  <Col sm={6}>
                    <div className="small text-muted">{t("kimlikDogrulama.pasaportNumarasi")}</div>
                    <div className="font-monospace">{detay.pasaportNo || "—"}</div>
                  </Col>
                  <Col sm={6}>
                    <div className="small text-muted">{t("kimlikDogrulama.verenUlke")}</div>
                    <div>{ulkeId != null ? (ulkeAdlari[String(ulkeId)] ?? `#${ulkeId}`) : "—"}</div>
                  </Col>
                </Row>
                {belgeAlani}
                {durumNo === 3 && (
                  <Alert fade={false} color="info" className="small py-2 mt-2 mb-0">
                    {t("kimlikDogrulama.masadaOnaylandi", { dogrulayanAdi: detay.dogrulayanAdi ?? "—", tarih: tarihYazisi })}
                  </Alert>
                )}
                {/* Doğrulanmış kişide bölüm salt okunur (backend 409 PERSONEL_PASAPORT_BELGESI_KIMLIK_DOGRULANMIS); durum 3'te yalnızca red kalır. */}
                {durumNo !== 1 && (
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {durumNo !== 3 && (
                      <Button color="success" outline size="sm" disabled={busy || !detay.pasaportNo} onClick={() => belgeKarari(1)}>
                        <CheckCircle2 size={14} className="me-1" />{t("kimlikDogrulama.belgeyiOnayla")}
                      </Button>
                    )}
                    <Button color="danger" outline size="sm" disabled={busy} onClick={() => belgeKarari(2)}>
                      <XCircle size={14} className="me-1" />{t("kimlikDogrulama.belgeyiReddet")}
                    </Button>
                  </div>
                )}
                {durumNo !== 1 && !detay.pasaportNo && (
                  <div className="form-text text-warning">{t("kimlikDogrulama.pasaportNoYok")}</div>
                )}
              </div>
            ) : (
              <>
                {/* Tek ortak Kimlik belgesi alanı: belge varsa kilitli kutu, yoksa yükleme (#470). */}
                <Label className="small mb-1 d-block fw-semibold">{t("kimlikDogrulama.kimlikBelgesi")}</Label>
                {belgeAlani}
              </>
            )}

            <Label className="small mb-1 mt-3 d-block fw-semibold">{t("kimlikDogrulama.dogrulamaTipi")}</Label>
            <div className="d-flex flex-wrap gap-2">
              {KIMLIK_BELGE_TURLERI.map((tur) => {
                const secili = belgeTuru === tur.value;
                const kilitli = tur.dosyaGerekir && !belge;
                return (
                  // Pasif düğme fare olaylarını yutar; ipucu için sarmalayıcıya title.
                  <span key={tur.value} title={kilitli ? t("kimlikDogrulama.tipDosyaGerekir") : undefined}>
                    <Button
                      size="sm" color="primary" outline={!secili} active={secili} aria-pressed={secili}
                      disabled={busy || kilitli} onClick={() => tipSec(tur)}
                    >
                      {tur.label}
                    </Button>
                  </span>
                );
              })}
            </div>
            {pasaportSecili && !pasaportBolumu && !detay.pasaportNo && (
              <div className="form-text text-warning">{t("kimlikDogrulama.pasaportNoYok")}</div>
            )}
            {/* #664 / #719: yalnızca Pasaport. Alt çubuktaki karar kapıdaki karardır: kutu işaretlenmeden "Kimliği doğrula" ve Reddet kapalı. */}
            {pasaportSecili && (
              <>
                <div className="form-check mt-2">
                  <Input
                    type="checkbox" id="kimlikYuzYuze" checked={yuzYuze} disabled={busy}
                    onChange={(e) => setYuzYuze(e.target.checked)}
                  />
                  <Label className="form-check-label" htmlFor="kimlikYuzYuze">{t("kimlikDogrulama.yuzYuzeGordum")}</Label>
                </div>
                {!yuzYuze && <div className="form-text">{t("kimlikDogrulama.yuzYuzeIpucu")}</div>}
              </>
            )}

            {aciklamaAcik ? (
              <>
                <Label className="small mb-1 mt-3" htmlFor="kimlikDogrulamaAciklama">{t("kimlikDogrulama.aciklama")}</Label>
                <Input
                  id="kimlikDogrulamaAciklama" type="textarea" rows={2} maxLength={500} value={aciklama}
                  autoFocus={aciklamaOdak}
                  onChange={(e) => setAciklama(e.target.value)}
                />
              </>
            ) : (
              <div className="mt-3">
                <Button
                  color="link" size="sm" className="p-0"
                  onClick={() => { setAciklamaAcik(true); setAciklamaOdak(true); }}
                >
                  {t("kimlikDogrulama.aciklamaGir")}
                </Button>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>{t("common:kapat")}</Button>
        {detay && detay.durum !== 0 && (
          <Button color="warning" outline disabled={busy} onClick={() => submit(0)}>
            <RotateCcw size={14} className="me-1" />{t("kimlikDogrulama.sifirla")}
          </Button>
        )}
        <Button color="danger" outline disabled={busy || kapiKilitli} onClick={() => submit(2)}>
          <ShieldX size={14} className="me-1" />{t("kimlikDogrulama.reddet")}
        </Button>
        {/* WAS (until #470): disabled={busy || detay?.durum === 1} — doğrulanmış kişide Onayla kapalıydı; artık tipi / açıklamayı güncellemek için açık. */}
        <Button color="success" disabled={busy || kapiKilitli} onClick={() => submit(1)}>
          {saving ? <Spinner size="sm" /> : (
            <>
              <ShieldCheck size={14} className="me-1" />
              {detay?.durum === 1 ? t("kimlikDogrulama.dogrulamayiGuncelle") : pasaportSecili ? t("kimlikDogrulama.kimligiDogrula") : t("kimlikDogrulama.onayla")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
